import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import colors from '../../../../../../config/theme/colors';
import {
  generatorsWithOneConnector,
  generatorsWithTwoConnectors,
  leadsWithOneConnector,
  leadsWithTwoConnectors,
} from 'Routes/Vns/Document/config';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TEpilepsyESVnsTreatmentType } from '../../definitions/epilepsy';
import { ClearIconStyled } from '..';
import { Container, Item } from 'Components/Grid';

const vnsLeadTypeCategories = [
  { title: 'vns.leadsWithOneConnector', values: leadsWithOneConnector },
  { title: 'vns.leadsWithTwoConnectors', values: leadsWithTwoConnectors },
];

const VNSTreatmentField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const current: TEpilepsyESVnsTreatmentType =
    searchFields.vnsTreatment !== 'noCropping' ? searchFields.vnsTreatment : {};
  const status = current.status ?? [];

  const onChangeStatus = (values: TOnChangeValues) => {
    const newStatus = values['status'] ?? [];
    if (Array.isArray(newStatus) && newStatus.length === 0) {
      onChange({ vnsTreatment: 'noCropping' });
    } else {
      onChange({ vnsTreatment: { ...current, status: newStatus } });
    }
  };

  const onChangeLeadType = (values: TOnChangeValues) => {
    const newLeadType = values['leadType'] as IVNSLead['leadType'] | null;
    onChange({ vnsTreatment: { ...current, leadType: newLeadType ?? undefined } });
  };

  const onChangeGenerator = (values: TOnChangeValues) => {
    onChange({ vnsTreatment: { ...current, generator: (values['generator'] as string) ?? undefined } });
  };

  const generatorOptions = [...generatorsWithOneConnector, ...generatorsWithTwoConnectors];

  return (
    <React.Fragment>
      <InputHandler
        type="Checkbox"
        editing={true}
        name="status"
        formData={{
          onChange: onChangeStatus,
          document: { status },
        }}
        options={['ongoing', 'ended']}
        optionFormatter={(name: string | number) => fm(`extendedSearch.opts.${name}`)}
      />
      {status.length > 0 && (
        <React.Fragment>
          <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 0.5rem 0' }}>
            {fm('extendedSearch.leadType')}
          </div>
          <Container style={{ alignItems: 'center' }}>
            <Item>
              <InputHandler
                type="SelectByCategory"
                editing={true}
                name="leadType"
                formData={{
                  onChange: onChangeLeadType,
                  document: { leadType: current.leadType ?? '' },
                }}
                options={vnsLeadTypeCategories}
                optionFormatter={(name: string | number) => {
                  const n = `${name}`;
                  if (n.startsWith('unspecified')) return formatMessage({ id: `vns.opts.leadType.${n}` });
                  return n;
                }}
                placeholder="vns.chooseLeadType"
                formatTitle={true}
              />
            </Item>
            {current.leadType && (
              <Item style={{ marginLeft: '2rem' }}>
                <ClearIconStyled onClick={() => onChange({ vnsTreatment: { ...current, leadType: undefined } })} />
              </Item>
            )}
          </Container>
          <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 0.5rem 0' }}>
            {fm('extendedSearch.generator')}
          </div>
          <Container style={{ alignItems: 'center' }}>
            <Item>
              <InputHandler
                type="Select"
                editing={true}
                name="generator"
                formData={{
                  onChange: onChangeGenerator,
                  document: { generator: current.generator ?? null },
                }}
                options={generatorOptions}
                optionFormatter={(name: string | number): string => `${name}`}
                placeholder="vns.chooseGenerator"
                fittedWidth
              />
            </Item>
            {current.generator && (
              <Item style={{ marginLeft: '2rem' }}>
                <ClearIconStyled onClick={() => onChange({ vnsTreatment: { ...current, generator: undefined } })} />
              </Item>
            )}
          </Container>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default VNSTreatmentField;
